import { supabase } from './supabase';

const BUCKET = 'district-documents';

function categoryToFolder(category) {
  const map = {
    'Church Report': 'church-reports',
    'District Report': 'district-reports',
    'Ministry Report': 'ministry-reports',
    'Annual Report': 'annual-reports',
    'Meeting Document': 'meeting-documents',
    Form: 'forms',
    Newsletter: 'newsletters',
    Other: 'other',
  };
  return map[category] || 'other';
}

function slugify(value) {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

/**
 * Uploads a file to Storage, then writes its metadata row.
 * Throws on failure. If the metadata insert fails after the file
 * uploaded, the uploaded object is removed so storage and the DB
 * don't drift out of sync.
 */
export async function uploadDocument({
  file,
  title,
  category,
  churchId,
  churchName,
  year,
  description,
  userId,
}) {
  const folder = categoryToFolder(category);
  const churchFolder = churchId ? slugify(churchName || churchId) : 'district-wide';
  const extension = file.name.split('.').pop();
  const uniqueName = `${Date.now()}-${slugify(title || file.name)}.${extension}`;
  const filePath = `${folder}/${churchFolder}/${uniqueName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(filePath, file, { cacheControl: '3600', upsert: false });

  if (uploadError) {
    throw new Error('We couldn\'t upload this document. Please try again.');
  }

  const { data, error: insertError } = await supabase
    .from('documents')
    .insert({
      title,
      file_name: file.name,
      file_path: filePath,
      file_type: extension,
      file_size: file.size,
      category,
      church_id: churchId || null,
      church_name: churchId ? churchName : null,
      year: year || null,
      description: description || null,
      uploaded_by: userId,
    })
    .select()
    .single();

  if (insertError) {
    // Roll back the orphaned file so storage doesn't accumulate
    // files with no matching metadata record.
    await supabase.storage.from(BUCKET).remove([filePath]);
    throw new Error('We couldn\'t save this document\'s details. Please try again.');
  }

  return data;
}

/**
 * Lists documents with optional search/filter, newest first.
 */
export async function listDocuments({ search, category, churchId, year, limit } = {}) {
  let query = supabase.from('documents').select('*').order('created_at', { ascending: false });

  if (search) {
    query = query.ilike('title', `%${search}%`);
  }
  if (category) {
    query = query.eq('category', category);
  }
  if (churchId) {
    query = query.eq('church_id', churchId);
  }
  if (year) {
    query = query.eq('year', year);
  }
  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;
  if (error) {
    throw new Error('We couldn\'t load documents right now. Please try again.');
  }
  return data;
}

/**
 * Simple counts for the dashboard overview cards.
 */
export async function getDocumentStats() {
  const counts = {};
  const categories = ['Church Report', 'District Report', 'Ministry Report'];

  const { count: total, error: totalError } = await supabase
    .from('documents')
    .select('*', { count: 'exact', head: true });
  if (totalError) throw new Error('We couldn\'t load dashboard stats.');
  counts.total = total || 0;

  for (const category of categories) {
    const { count, error } = await supabase
      .from('documents')
      .select('*', { count: 'exact', head: true })
      .eq('category', category);
    if (error) throw new Error('We couldn\'t load dashboard stats.');
    counts[category] = count || 0;
  }

  return counts;
}

/**
 * Generates a short-lived signed URL for viewing/downloading a private file.
 */
export async function getSignedUrl(filePath, expiresInSeconds = 60) {
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(filePath, expiresInSeconds);
  if (error) {
    throw new Error('We couldn\'t open this document. Please try again.');
  }
  return data.signedUrl;
}

/**
 * Deletes the storage object first, then the metadata row.
 * If the DB delete fails after the file is gone, we surface that
 * distinctly so an admin can retry cleanup rather than silently
 * leaving a dangling row.
 */
export async function deleteDocument(doc) {
  const { error: storageError } = await supabase.storage.from(BUCKET).remove([doc.file_path]);
  if (storageError) {
    throw new Error('We couldn\'t delete this file. Please try again.');
  }

  const { error: dbError } = await supabase.from('documents').delete().eq('id', doc.id);
  if (dbError) {
    throw new Error(
      'The file was removed from storage, but we couldn\'t update its record. Please refresh and try again.'
    );
  }
}
